import React from "react";
import {
  ServerIcon,
  TrashIcon,
  RefrigeratorIcon,
  CarIcon,
  ShoppingCartIcon,
  FlaskConicalIcon,
  BuildingIcon,
  DropletIcon,
  BarChart3Icon,
  DatabaseIcon,
  CodeIcon,
  CpuIcon,
  WorkflowIcon,
  BotIcon,
} from "lucide-react";

import WetLabEquipment from "../../../assets/wet-lab-equipment.webp";
import DryLabEquipment from "../../../assets/dry-lab-equipment.webp";

interface CategoryIconProps {
  icon: React.ReactNode;
  label: string;
}

const CategoryIcon: React.FC<CategoryIconProps> = ({ icon, label }) => {
  return (
    <div className="flex flex-col items-center group">
      <div className="w-6 h-6 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center mb-1 sm:mb-3 border-2 sm:border-3 md:border-4 border-[#e3f1e3] bg-[#f7fbf7] transition-transform duration-200 group-hover:scale-105">
        {icon}
      </div>
      <span className="text-green-600 text-[10px] sm:text-sm text-center font-medium leading-tight">
        {label}
      </span>
    </div>
  );
};

const WebLabSustainabilitySection: React.FC = () => {
  return (
    <div className="max-w-4xl w-full mx-auto mb-16 px-4 sm:px-0">
      <div className="flex flex-row gap-2 sm:gap-6 lg:gap-8 items-end">
        {/* Bronze Level */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 md:gap-6 mb-4 sm:mb-8">
            <CategoryIcon
              icon={
                <ServerIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Hardware"
            />
            <CategoryIcon
              icon={
                <TrashIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Lab Waste"
            />
            <CategoryIcon
              icon={
                <RefrigeratorIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Cold Storage"
            />
          </div>
          <div className="bg-[#e9b382] p-4 sm:p-6 md:p-8 rounded-lg shadow-sm flex items-center justify-center h-20 sm:h-24 md:h-28 lg:h-32 transition-shadow duration-200 hover:shadow-md">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#8b5e34] tracking-wide">
              Bronze
            </h2>
          </div>
        </div>
        {/* Silver Level */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 md:gap-6 mb-4 sm:mb-8">
            <CategoryIcon
              icon={
                <CarIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Transport"
            />
            <CategoryIcon
              icon={
                <ShoppingCartIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Purchasing"
            />
            <CategoryIcon
              icon={
                <FlaskConicalIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Chemistry"
            />
          </div>
          <div className="bg-[#c4c4c4] p-4 sm:p-6 md:p-8 rounded-lg shadow-sm flex items-center justify-center h-24 sm:h-28 md:h-36 lg:h-40 transition-shadow duration-200 hover:shadow-md">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#666666] tracking-wide">
              Silver
            </h2>
          </div>
        </div>
        {/* Gold Level */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 md:gap-6 mb-4 sm:mb-8">
            <CategoryIcon
              icon={
                <BuildingIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Eco Offices"
            />
            <CategoryIcon
              icon={
                <DropletIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Water"
            />
            <CategoryIcon
              icon={
                <BarChart3Icon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Equipment"
            />
          </div>
          <div className="bg-[#f8e3a3] p-4 sm:p-6 md:p-8 rounded-lg shadow-sm flex items-center justify-center h-28 sm:h-32 md:h-44 lg:h-48 transition-shadow duration-200 hover:shadow-md">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#b39033] tracking-wide">
              Gold
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
};

const DryLabSustainabilitySection: React.FC = () => {
  return (
    <div className="max-w-4xl w-full mx-auto px-4 sm:px-0">
      <div className="flex flex-row gap-2 sm:gap-6 lg:gap-8 items-end">
        {/* Bronze Level */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 md:gap-6 mb-4 sm:mb-8">
            <CategoryIcon
              icon={
                <BuildingIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Eco Offices"
            />
            <CategoryIcon
              icon={
                <DatabaseIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Data Storage"
            />
            <CategoryIcon
              icon={
                <ServerIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Hardware"
            />
          </div>
          <div className="bg-[#e9b382] p-4 sm:p-6 md:p-8 rounded-lg shadow-sm flex items-center justify-center h-20 sm:h-24 md:h-28 lg:h-32 transition-shadow duration-200 hover:shadow-md">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#8b5e34] tracking-wide">
              Bronze
            </h2>
          </div>
        </div>
        {/* Silver Level */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 md:gap-6 mb-4 sm:mb-8">
            <CategoryIcon
              icon={
                <CarIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Transport"
            />
            <CategoryIcon
              icon={
                <CodeIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Green Code"
            />
            <CategoryIcon
              icon={
                <CpuIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Computing"
            />
          </div>
          <div className="bg-[#c4c4c4] p-4 sm:p-6 md:p-8 rounded-lg shadow-sm flex items-center justify-center h-24 sm:h-28 md:h-36 lg:h-40 transition-shadow duration-200 hover:shadow-md">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#666666] tracking-wide">
              Silver
            </h2>
          </div>
        </div>
        {/* Gold Level */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 md:gap-6 mb-4 sm:mb-8">
            <CategoryIcon
              icon={
                <WorkflowIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Workflows"
            />
            <CategoryIcon
              icon={
                <TrashIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="Digital Waste"
            />
            <CategoryIcon
              icon={
                <BotIcon className="text-green-500 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              }
              label="AI"
            />
          </div>
          <div className="bg-[#f8e3a3] p-4 sm:p-6 md:p-8 rounded-lg shadow-sm flex items-center justify-center h-28 sm:h-32 md:h-44 lg:h-48 transition-shadow duration-200 hover:shadow-md">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#b39033] tracking-wide">
              Gold
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
};

type SubscriptionChoicesProps = {
  readonly detailed?: boolean;
};

const SubscriptionChoices: React.FC<SubscriptionChoicesProps> = ({
  detailed = false,
}) => {
  return (
    <section className="secondary-page">
      <div className="max-w-6xl mx-auto">
        <h1>Subscription Choices</h1>
        {!detailed ? (
          <>
            <p className="mb-8 text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">
              Organisations can subscribe between 1-100 labs, with options to access our:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <fieldset className="bg-white/50 shadow-xl border-2 border-green-200 rounded-lg p-6">
                <legend className="px-4">
                  <span className="badge badge-primary badge-lg text-lg font-semibold text-white">Wet Lab Platform</span>
                </legend>
                <figure className="flex justify-center mb-4">
                  <img
                    src={WetLabEquipment}
                    alt="Wet Lab Equipment"
                    className="h-48 object-contain"
                  />
                </figure>
                <div className="space-y-4">
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    Research involving liquids, chemicals, or biological materials.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    Examples: chemistry, biology, medical research, microbiology.
                  </p>
                </div>
              </fieldset>
              <fieldset className="bg-white/50 shadow-xl border-2 border-green-200 rounded-lg p-6">
                <legend className="px-4">
                  <span className="badge badge-primary badge-lg text-lg font-semibold text-white">Dry Lab Platform</span>
                </legend>
                <figure className="flex justify-center mb-4">
                  <img
                    src={DryLabEquipment}
                    alt="Dry Lab Equipment"
                    className="h-48 object-contain"
                  />
                </figure>
                <div className="space-y-4">
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    Research using computers and data, not physical.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    Examples: bioinformatics, data analysis, computational modelling.
                  </p>
                </div>
              </fieldset>
              <fieldset className="bg-white/50 shadow-xl border-2 border-green-200 rounded-lg p-6">
                <legend className="px-4">
                  <span className="badge badge-primary badge-lg text-lg font-semibold text-white">Wet & Dry Lab Platform</span>
                </legend>
                <figure className="flex justify-center h-48 relative overflow-hidden mb-4">
                  <div className="relative w-full max-w-64 h-full">
                    <img
                      src={DryLabEquipment}
                      alt="Dry Lab Equipment"
                      className="absolute right-2 top-2 h-36 w-auto object-contain opacity-90"
                    />
                    <img
                      src={WetLabEquipment}
                      alt="Wet Lab Equipment"
                      className="absolute left-2 top-4 h-40 w-auto object-contain"
                    />
                  </div>
                </figure>
                <div className="space-y-4">
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      Combined physical experiments and data-driven research.
                  </p>
                </div>
              </fieldset>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-800 mb-4">
              Both lab subscriptions include:
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base md:text-lg text-gray-700">
              <li><b className="l0-emphasis">Baseline & savings assessments</b> - Track progress from start to finish</li>
              <li><b className="l0-emphasis">Actions Checklists</b> - Step-by-step instructions for your team</li>
              <li><b className="l0-emphasis">Estimate Impact Reports</b> - See the difference your lab or organisation is making</li>
              <li><b className="l0-emphasis">Resource Links</b> - Utilise ours or embed your organisations</li>
              <li><b className="l0-emphasis">Audit Tools</b> - Clear instructions for peer-to-peer certification review</li>
              <li><b className="l0-emphasis">Digital Certificates</b> - For teams who have successfully passed their audit</li>
            </ul>
            <div className="mb-8 sm:mb-12 mt-4 sm:mt-6">
              <p className="text-green-600 text-sm sm:text-base leading-relaxed font-normal mb-1">
                The sustainability categories covered are shown here,
              </p>
              <p className="text-green-600 text-sm sm:text-base leading-relaxed font-normal">
                organised in the order they appear at each certification level.
              </p>
            </div>
            <div className="space-y-8">
              <fieldset className="border-2 border-green-200 rounded-lg p-6 bg-white/50 shadow-sm">
                <legend className="px-4">
                  <span className="badge badge-primary badge-lg text-lg font-semibold text-white">Wet lab categories</span>
                </legend>
                <WebLabSustainabilitySection />
              </fieldset>
              <fieldset className="border-2 border-green-200 rounded-lg p-6 bg-white/50 shadow-sm">
                <legend className="px-4">
                  <span className="badge badge-primary badge-lg text-lg font-semibold text-white">Dry lab categories</span>
                </legend>
                <DryLabSustainabilitySection />
              </fieldset>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default SubscriptionChoices;
